import React, { memo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import type { ITeacherContact } from 'core/hooks/TeacherContacts';

export interface ITeacherSlackContacts {
  /** Пустой список — блок не рендерится (нет Slack у группы или связки преподавателя). */
  contacts: ITeacherContact[];
  className?: string;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

const TeacherAvatar = memo(({ photo, name }: { photo: string | null; name: string }) => {
  // Битая ссылка на фото — инициалы, а не значок сломанной картинки.
  const [failed, setFailed] = useState(false);
  const initials = initialsOf(name);

  if (photo && !failed) {
    return (
      <Avatar aria-hidden="true">
        <AvatarImage src={photo} alt="" onError={() => setFailed(true)} />
      </Avatar>
    );
  }
  if (!initials) {
    return null;
  }
  return (
    <Avatar aria-hidden="true">
      <Initials>{initials}</Initials>
    </Avatar>
  );
});

/**
 * #58 «Napisz do prowadzącego» — личка преподавателю в Slack группы.
 *
 * Только Slack, без почты (решение владельца 07.10). Главная ссылка открывает
 * приложение Slack (`slack://user`); если сервер знает адрес workspace,
 * под ней — запасная «Otwórz w przeglądarce» в новой вкладке.
 *
 * Подпись ссылки — видимый текст (имя + «Napisz w Slacku»), без aria-label:
 * читалка произносит ровно то, что видно. У нескольких преподавателей
 * одинаковые «Otwórz w przeglądarce» различает скрытое имя.
 */
export const TeacherSlackContacts = memo(({ contacts, className }: ITeacherSlackContacts) => {
  const { t } = useTranslation();

  if (!contacts.length) {
    return null;
  }

  const title =
    contacts.length > 1
      ? t('courseInfo.teacherContact.titleMany')
      : t('courseInfo.teacherContact.title');

  return (
    <Wrapper className={className} data-testid="teacher-slack-contacts">
      <Title>{title}</Title>
      <List>
        {contacts.map((contact) => {
          const name = contact.fullName || t('courseInfo.teacherContact.teacherFallback');
          return (
            <Item key={contact.teacherId}>
              <AppLink href={contact.slackAppUrl} data-testid="teacher-slack-app-link">
                <TeacherAvatar photo={contact.photo} name={contact.fullName} />
                <LinkText>
                  <Name>{name}</Name>
                  {/* Пробел — чтобы подпись ссылки читалась «Imię Nazwisko Napisz w Slacku»,
                      а не слитно: во флекс-колонке он не виден. */}{' '}
                  <Cta>{t('courseInfo.teacherContact.writeInSlack')}</Cta>
                </LinkText>
              </AppLink>
              {contact.slackWebUrl && (
                <WebLink
                  href={contact.slackWebUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="teacher-slack-web-link">
                  {t('courseInfo.teacherContact.openInBrowser')}
                  <VisuallyHidden>
                    {t('courseInfo.teacherContact.webLinkContext', { name })}
                  </VisuallyHidden>
                </WebLink>
              )}
            </Item>
          );
        })}
      </List>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 320px;
  /* Как у GroupServiceInfo: блок карточки курса поверх соседних слоёв. */
  position: relative;
  z-index: 2;
`;

const Title = styled.h4`
  margin: 0;
  ${fontService.text({ type: TextType.extraSmall })};
  color: ${Colors.gray_dark};
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const AppLink = styled.a`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 8px 16px;
  border-radius: 8px;
  /* ПРАВИЛО ЖЁЛТОГО (context/constants/colors.ts): на main — только btn_text. */
  background-color: ${Colors.main};
  color: ${Colors.btn_text};
  text-decoration: none;

  &:hover {
    transition: 0.5s;
    background-color: ${Colors.main_light};
    color: ${Colors.btn_hover_text};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;

const Avatar = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${Colors.white};
`;

const AvatarImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
  object-position: center;
`;

const Initials = styled.span`
  ${fontService.text({ size: 12, lineHeight: 100, weight: FontWeight.bold })};
  /* На белом кружке — не btn_text, а обычный тёмный текст темы. */
  color: ${Colors.black};
`;

const LinkText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Name = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })};
  word-break: break-word;
`;

const Cta = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })};
`;

const WebLink = styled.a`
  ${fontService.text({ type: TextType.small })};
  color: ${Colors.gray_dark};
  text-decoration: underline;

  &:hover {
    color: ${Colors.black};
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
